import React from 'react'
import {appName} from '../settings/constants'
import {createSelector} from 'reselect'
import {Record} from 'immutable'
import RestService from '../helpers/RestService'
import {arrayToMap, formatErrorMessage, middleReduxFormThen, middleThen} from '../helpers/common'
import {LANGUAGE_GUID_MAP} from '../settings/constants'
import {isAuthSelector, languageIdSelector, profileLoadStateSelector, uploadProfile} from './profile'
import {getLocalAppData, clearLocalAppData} from '../helpers/localData'
import {showModal, hideModal, INFO, INFO_TYPE_ERROR, INFO_TYPE_WARNING, POPOVER} from "./modals"
import {PAYPAL_DONATE_LINK, PAYPAL_DONATE_POPOVER_INIT_TIMER} from "../settings/settings"
import { SubmissionError } from 'redux-form'
import {ajaxUrls, outerUrls, routesUrls} from '../settings/routes'
import history from '../history'


/**
 * Constants
 * */

export const moduleName = 'app'
const prefix = `${appName}/${moduleName}`

export const SET_IS_INITED = `${prefix}/SET_IS_INITED`
export const SET_TRANSLATIONS = `${prefix}/SET_TRANSLATIONS`
export const SET_APP_LANG_ID = `${prefix}/SET_APP_LANG_ID`
export const SET_LANGUAGES = `${prefix}/SET_LANGUAGES`
export const SET_HAD_LOGIN_SESSION = `${prefix}/SET_HAD_LOGIN_SESSION`
export const SET_WAS_OFFLINE = `${prefix}/SET_WAS_OFFLINE`
export const SET_WAS_DONATE_POPUP = `${prefix}/SET_WAS_DONATE_POPUP`
export const SET_FORBID_CLICK = `${prefix}/SET_FORBID_CLICK`
export const SET_WAS_ASKED_TUTORIAL = `${prefix}/SET_WAS_ASKED_TUTORIAL`
export const SET_QBM_NAV_ITEM = `${prefix}/SET_QBM_NAV_ITEM`
export const SET_TRANSLATION_BY_MESSAGE_ID = `${prefix}/SET_TRANSLATION_BY_MESSAGE_ID`
export const DELETE_TRANSLATION_BY_MESSAGE_ID = `${prefix}/DELETE_TRANSLATION_BY_MESSAGE_ID`
export const ADMIN_SETTINGS_START = `${prefix}/ADMIN_SETTINGS_START`
export const ADMIN_SETTINGS_SUCCESS = `${prefix}/ADMIN_SETTINGS_SUCCESS`
export const ADMIN_SETTINGS_FAIL = `${prefix}/ADMIN_SETTINGS_FAIL`
export const ADMIN_SETTINGS_SET = `${prefix}/ADMIN_SETTINGS_SET`
export const SET_CAPTCHA_HASH = `${prefix}/SET_CAPTCHA_HASH`


const hadLoginSessionDefault = false
const wasOfflineDefault = false
export const NO_ACTION_SECONDS_LOGOUT = 900
const wasDonatePopupDefault = false


export const QBM_NAV_SUBJECTS = "Subjects"
export const QBM_NAV_CATEGORIES = "Categories"
export const QBM_NAV_SOURCES = "Sources"
export const QBM_NAV_TRASHED = "Trashed"
export const QBM_NAV_TEMP = "Temp"

const qbmNavDefault = QBM_NAV_SUBJECTS

/**
 * Reducer
 * */

const languageRecord = Record({
    id : null,
    name : "",
})

const translationsRecord = Record({
    loaded : false,
    data : {},
    appLanguageId : null,
    languages : arrayToMap([], languageRecord, "id"),
})

const adminDataRecord = Record({
    senderAdminEmail : null,
    receiverAdminEmail : null,
    supportEmail : null,
    notifyAboutNewUser : null,
})

const adminSettings = Record({
    loading : false,
    loaded : false,
    data : new adminDataRecord()
})

export const ReducerRecord = Record({
    isInited : false,

    translations : new translationsRecord(),
    hadLoginSession : hadLoginSessionDefault,
    wasOffline : wasOfflineDefault,
    wasDonatePopup : wasDonatePopupDefault,
    forbidClick : false,
    wasAskedTutorial : false,

    qbmNavItem : qbmNavDefault,

    adminSettings : new adminSettings(),

    captchaHash : null,
})

const defaultState = new ReducerRecord()

export default function reducer(state = defaultState, action) {
    const {type, payload} = action

    switch (type) {
        case SET_IS_INITED : {
            return state.set('isInited', true)
            break
        }
        case SET_TRANSLATIONS : {
            let {translations, appLanguageId} = payload
            return state.setIn(['translations', 'data'], translations)
                        .setIn(['translations', 'appLanguageId'], appLanguageId)
                        .setIn(['translations', 'loaded'], true)
            break
        }
        case SET_APP_LANG_ID : {
            let {appLanguageId} = payload
            return state.setIn(['translations', 'appLanguageId'], appLanguageId)
            break
        }
        case SET_LANGUAGES : {
            let {languages} = payload
            return state.updateIn(['translations', 'languages'], currLanguages => currLanguages.merge(arrayToMap(languages, languageRecord, "id")))
            break
        }
        case SET_HAD_LOGIN_SESSION : {
            let {hadLoginSession} = payload
            return state.set('hadLoginSession', hadLoginSession)
            break
        }
        case SET_WAS_OFFLINE : {
            let {wasOffline} = payload
            return state.set('wasOffline', wasOffline)
            break
        }
        case SET_WAS_DONATE_POPUP : {
            let {wasDonatePopup} = payload
            return state.set('wasDonatePopup', wasDonatePopup)
            break
        }
        case SET_FORBID_CLICK : {
            let {forbidClick} = payload
            return state.set('forbidClick', forbidClick)
            break
        }
        case SET_WAS_ASKED_TUTORIAL : {
            let {wasAskedTutorial} = payload
            return state.set('wasAskedTutorial', wasAskedTutorial)
            break
        }
        case SET_QBM_NAV_ITEM : {
            const {qbmNavItem} = payload
            return state.set('qbmNavItem', qbmNavItem)
            break
        }
        case SET_TRANSLATION_BY_MESSAGE_ID : {
            const {messageId, en, de} = payload
            return state.updateIn(['translations', 'data'], translations => ({
                ...translations,
                en : {
                    ...translations.en,
                    [messageId] : en,
                },
                de : {
                    ...translations.de,
                    [messageId] : de,
                }
            }))
            break
        }
        case DELETE_TRANSLATION_BY_MESSAGE_ID : {
            const {messageId} = payload
            return state.updateIn(['translations', 'data'], translations => {
                let t = JSON.parse(JSON.stringify(translations))
                delete t.en[messageId]
                delete t.de[messageId]
                return t
            })
            break
        }
        case ADMIN_SETTINGS_START : {
            return state.setIn(['adminSettings', 'loading'], true)
            break
        }
        case ADMIN_SETTINGS_SUCCESS : {
            return _adminSettingsSetReducer(state, payload)
                    .setIn(['adminSettings', 'loading'], false)
                    .setIn(['adminSettings', 'loaded'], true)
            break
        }
        case ADMIN_SETTINGS_FAIL : {
            return state.setIn(['adminSettings', 'loading'], false).setIn(['adminSettings', 'loaded'], false)
            return state
            break
        }
        case ADMIN_SETTINGS_SET : {
            return _adminSettingsSetReducer(state, payload)
            break
        }
        case SET_CAPTCHA_HASH : {
            let {hash} = payload
            return state.set('captchaHash', hash)
            break
        }
        default : {
            return state
            break
        }
    }
}

const _adminSettingsSetReducer = (state, payload) => {
    const {senderAdminEmail, receiverAdminEmail, notifyAboutNewUser, supportEmail} = payload
    return state
        .updateIn(['adminSettings', 'data'], data => {
            if (typeof senderAdminEmail !== 'undefined') data = data.set('senderAdminEmail', senderAdminEmail)
            if (typeof receiverAdminEmail !== 'undefined') data = data.set('receiverAdminEmail', receiverAdminEmail)
            if (typeof notifyAboutNewUser !== 'undefined') data = data.set('notifyAboutNewUser', notifyAboutNewUser)
            if (typeof supportEmail !== 'undefined') data = data.set('supportEmail', supportEmail)
            return data
        })
}


/**
 * Selectors
 * */

export const stateSelector = state => state[moduleName]
export const isInitedSelector = createSelector(stateSelector, state => state.isInited)
export const translationsSelector = createSelector(stateSelector, state => state.translations)
export const translationsLoadedStateSelector = createSelector(translationsSelector, translationsApp => translationsApp.loaded)
export const appLanguageIdSelector = createSelector(translationsSelector, translationsApp => translationsApp.appLanguageId)
export const translationsLocaleSelector = createSelector(translationsSelector, appLanguageIdSelector, languageIdSelector, (translationsApp, appLanguageId, languageId) => {
    let id = languageId ? languageId : appLanguageId
    return LANGUAGE_GUID_MAP[id] ? LANGUAGE_GUID_MAP[id].locale : 'en'
})
export const allTranslationsDataSelector = createSelector(translationsSelector, translationsApp => translationsApp.data)
export const translationsDataSelector = createSelector(translationsSelector, appLanguageIdSelector, languageIdSelector, (translationsApp, appLanguageId, languageId) => {
    let id = languageId ? languageId : appLanguageId
    if (LANGUAGE_GUID_MAP[id]) return translationsApp.data[LANGUAGE_GUID_MAP[id].locale]
})

export const translationsByMessageIdMapSelector = createSelector(allTranslationsDataSelector, translationsData => {
    let translationsByMessageId = {}
    Object.keys(translationsData).forEach(language => {
        for (let messageId in translationsData[language]) {
            if (!translationsByMessageId[messageId]) {
                translationsByMessageId[messageId] = {
                    messageId,
                    [language] : translationsData[language][messageId]
                }
            } else {
                translationsByMessageId[messageId] = {
                    ...translationsByMessageId[messageId],
                    [language] : translationsData[language][messageId]
                }
            }
        }
    })
    return translationsByMessageId
})

export const translationsByMessageIdSelector = createSelector(translationsByMessageIdMapSelector, translationsByMessageIdMap => Object.values(translationsByMessageIdMap))

export const translationsMessageIdsSelector = createSelector(translationsByMessageIdMapSelector, translationsByMessageIdMap => Object.keys(translationsByMessageIdMap))

export const _getId = (_, id) => id
export const translationItemByMessageIdSelector = createSelector(translationsByMessageIdMapSelector, _getId, (translationsByMessageId, messageId) => messageId ? translationsByMessageId[messageId] : undefined)

export const languagesMapSelector = createSelector(translationsSelector, state => state.languages)
export const languagesSelector = createSelector(languagesMapSelector, languagesMap => languagesMap.valueSeq().toArray())
export const hadLoginSessionSelector = createSelector(stateSelector, state => state.hadLoginSession)
export const wasOfflineSelector = createSelector(stateSelector, state => state.wasOffline)
export const wasDonatePopupSelector = createSelector(stateSelector, state => state.wasDonatePopup)
export const forbidClickSelector = createSelector(stateSelector, state => state.forbidClick)
export const wasAskedTutorialSelector = createSelector(stateSelector, state => state.wasAskedTutorial)

export const qbmNavItemSelector = createSelector(stateSelector, state => state.qbmNavItem)

export const adminSettingsLoadingStateSelector = createSelector(stateSelector, state => ({
    loading : state.adminSettings.loading,
    loaded : state.adminSettings.loaded,
}))

export const adminSettingsDataSelector = createSelector(stateSelector, state => state.adminSettings.data)
export const captchaHashSelector = createSelector(stateSelector, state => state.captchaHash)


/**
 * Action Creators
 * */

export const setForbidClick = forbidClick => ({
    type : SET_FORBID_CLICK,
    payload : {
        forbidClick
    }
})

export const setQbmNavItem = qbmNavItem => ({
    type : SET_QBM_NAV_ITEM,
    payload : {
        qbmNavItem
    }
})

export const setTranslationByMessageId = ({messageId, en, de}) => ({
    type : SET_TRANSLATION_BY_MESSAGE_ID,
    payload : {
        messageId,
        en,
        de,
    }
})

export const deleteTranslationByMessageId = ({messageId}) => ({
    type : DELETE_TRANSLATION_BY_MESSAGE_ID,
    payload : {
        messageId
    }
})

export const adminSettingsSet = ({senderAdminEmail, receiverAdminEmail, supportEmail, notifyAboutNewUser}) => ({
    type : ADMIN_SETTINGS_SET,
    payload : {
        senderAdminEmail,
        receiverAdminEmail,
        supportEmail,
        notifyAboutNewUser,
    }
})


/**
 * Thunk
 * */

export const handleInit = () => {
    return (dispatch, getState) => {
        dispatch(localDataToStore())

        const state = getState()
        const isInited = isInitedSelector(state)
        const {loaded : profileLoaded, loading : profileLoading} = profileLoadStateSelector(state)
        const wasOffline = wasOfflineSelector(state)
        const isAuth = isAuthSelector(state)

        if (!isInited) {
            return new Promise(resolve => {
                dispatch(loadCommon(resolve))
            })
                .then(() => {
                    return new Promise(resolve => {
                        if (!profileLoading && !profileLoaded) {
                            dispatch(uploadProfile(resolve))
                        } else {
                            resolve()
                        }
                    })
                })
                .then(() => {
                    if (wasOffline) dispatch(logout({expired : {timeout : false}}))
                })
                .then(() => {
                    if (isAuth) dispatch(handleInitDonatePopup())
                })
                .finally(() => {
                    dispatch({
                        type : SET_IS_INITED
                    })
                })
        }
    }
}

export const handleInitDonatePopup = () => (dispatch, getState) => {
    // popup donate modal
    return null;
    const state = getState()
    const wasDonatePopup = wasDonatePopupSelector(state)
    if (!wasDonatePopup) {
        dispatch(handleSetAppData({wasDonatePopup : true}))
        setTimeout(() => {
            dispatch(showModal({
                modalType : POPOVER,
                modalProps : {
                    content : "Do You like Question Bank Manager? We rely on donations to carry out our mission to keep Question Bank Manager open and free. Will you give today?",
                    style : {
                        display : "block",
                        position : "fixed",
                        top : 'auto',
                        left : 'auto',
                        bottom : 0,
                        right : 0
                    },
                    okCB : () => {
                        dispatch(hideModal({modalType : POPOVER}))
                        history.push('/payment')
                    },
                    okButtonTxt : "Donate",
                }
            }))
        }, PAYPAL_DONATE_POPOVER_INIT_TIMER)
    }
}

export const localDataToStore = () => dispatch => {
    const app = getLocalAppData()
    if (app) {
        const {hadLoginSession, wasOffline, wasDonatePopup, wasAskedTutorial} = app
        dispatch({
            type : SET_HAD_LOGIN_SESSION,
            payload : {
                hadLoginSession : !!hadLoginSession,
            }
        })
        dispatch({
            type : SET_WAS_OFFLINE,
            payload : {
                wasOffline : !!wasOffline
            }
        })
        dispatch({
            type : SET_WAS_DONATE_POPUP,
            payload : {
                wasDonatePopup : !!wasDonatePopup
            }
        })
        dispatch({
            type : SET_WAS_ASKED_TUTORIAL,
            payload : {
                wasAskedTutorial : !!wasAskedTutorial
            }
        })
    }
}
export const handleSetAppData = params => dispatch => {
    const {hadLoginSession, wasOffline, wasDonatePopup, wasAskedTutorial} = params
    if (typeof hadLoginSession !== 'undefined') {
        dispatch({
            type : SET_HAD_LOGIN_SESSION,
            payload : {
                hadLoginSession : !!hadLoginSession,
            }
        })
    }

    if (typeof wasOffline !== 'undefined') {
        dispatch({
            type : SET_WAS_OFFLINE,
            payload : {
                wasOffline : !!wasOffline
            }
        })
    }

    if (typeof wasDonatePopup !== 'undefined') {
        dispatch({
            type : SET_WAS_DONATE_POPUP,
            payload : {
                wasDonatePopup : !!wasDonatePopup,
            }
        })
    }

    if (typeof wasAskedTutorial !== 'undefined') {
        dispatch({
            type : SET_WAS_ASKED_TUTORIAL,
            payload : {
                wasAskedTutorial : !!wasAskedTutorial,
            }
        })
    }
}
export const handleClearLocalAppData = () => dispatch => {
    dispatch({
        type : SET_HAD_LOGIN_SESSION,
        payload : {
            hadLoginSession : hadLoginSessionDefault,
        }
    })
    dispatch({
        type : SET_WAS_OFFLINE,
        payload : {
            wasOffline : wasOfflineDefault,
        }
    })
    dispatch({
        type : SET_WAS_DONATE_POPUP,
        payload : {
            wasDonatePopup : wasDonatePopupDefault,
        }
    })
    clearLocalAppData()
}

export const loadCommon = (resolve) => {
    return (dispatch) => {
        RestService.customFetch(ajaxUrls.common)
            .then(middleThen)
            .then(response => {
                let {errcode, translations, appLanguageId} = response
                if (errcode !== 200) {
                    throw new Error('somethingWentWrong')
                    return
                }

                dispatch({
                    type : SET_TRANSLATIONS,
                    payload : {
                        translations,
                        appLanguageId
                    }
                })
            })
            .catch(error => {
                console.warn(error) // TODO discuss with backend error handling
            })
            .finally(() => {
                if (resolve) resolve()
            })
    }
}

export const logout = (params = {}) => {
    return (dispatch, getState) => {

        const state = getState()
        const isAuth = isAuthSelector(state)

        if (!isAuth) return

        let {expired} = params
        if (!expired) dispatch(setForbidClick(true))

        //RestService.customFetch(ajaxUrls.siteLogout)
        RestService.customFetch(ajaxUrls.authLogout)
            .then(middleThen)
            .then(response => {
                let {errcode} = response
                if (errcode !== 200) {
                    throw new Error('failToLogout')
                    return
                }
            })
            .catch((error) => {
                console.warn(error)
                dispatch(setForbidClick(false))
            })
            .finally(() => {
                dispatch(handleClearLocalAppData())
                if (expired) {
                    let {timeout} = expired
                    dispatch(showModal({
                        modalType : INFO,
                        modalProps : {
                            title : "Session expiration",
                            text : `Sorry, session is expired because of ${timeout ? "inactivity" : "failed internet connection"}`,
                            infoType : INFO_TYPE_WARNING,
                            successCB : () => {
                                window.location.href = "/"
                                // history.push('/auth/login')
                            }
                        }
                    }))
                } else {
                    // history.push('/auth/login')
                    window.location.href = "/"
                    // window.location.href = outerUrls.home
                }
            })
    }
}

export const handleNonAuthLang = (lang) => dispatch => {
    let data = new FormData()
    data.append('lang', lang)
    RestService.bodyAsFormData(ajaxUrls.commonNonAuthLang, data)
        .then(middleThen)
        .then(response => {
            let {errcode, message} = response

            if (errcode !== 200) {
                throw new Error(formatErrorMessage(message))
                return
            }

            let langData = Object.values(LANGUAGE_GUID_MAP).find(({code}) => code === lang)
            if (!langData) {
                throw new Error(formatErrorMessage('checkLangGuidMap'))
                return
            }

            dispatch({
                type : SET_APP_LANG_ID,
                payload : {
                    appLanguageId : langData.id
                }
            })
        })
        .catch(error => {
            console.log(error)
        })
}

export const handleCaptchaInit = () => (dispatch, getState) => {
    const state = getState()
    const captchaHash = captchaHashSelector(state)
    if (!captchaHash) {
        dispatch(handleGetCaptchaHash())
    }
}

export const handleGetCaptchaHash = cb => dispatch => {
    fetch(routesUrls.captchaGetHash)
        .then(middleThen)
        .then(response => {
            let {errcode, payload : {hash1, hash2, url} = {}} = response
            if (errcode !== 200) {
                throw new Error('somethingWentWrong')
                return
            }
            dispatch({
                type : SET_CAPTCHA_HASH,
                payload : {
                    hash : hash1,
                }
            })
        })
        .catch(error => {
            console.log(error)
        })
        .finally(() => {
            typeof cb === 'function' && cb()
        })
}

export const handleRefreshCaptcha = cb => dispatch => {
    fetch(routesUrls.captchaRefresh).finally(() => {
        dispatch(handleGetCaptchaHash(cb))
    })
}



/*
* redux-form
* */

export const handleCrudTranslation = (params = {}) => {
    let {messageId, en, de, deleting} = params

    let url = deleting ? ajaxUrls.adminDeleteTranslation : ajaxUrls.adminCruTranslation
    let _body = deleting ? { messageId } : { messageId, translations : { en, de } }

    let data = new FormData()
    data.append('translation', JSON.stringify(_body))

    return RestService.bodyAsFormData(url, data)
        .then(middleReduxFormThen)
        .then(res => {
            let {errcode, message = ""} = res

            if (errcode === 200) {
                return 'ok'
            } else {
                throw new SubmissionError({
                    _error : formatErrorMessage(message),
                })
            }
        })
}


export const handleLoadAdminSettings = () => (dispatch, getState) => {
    const state = getState()
    const {loading, loaded} = adminSettingsLoadingStateSelector(state)

    if (loading || loaded) return

    dispatch({
        type : ADMIN_SETTINGS_START
    })

    return RestService.bodyAsFormData(ajaxUrls.adminParams)
        .then(middleThen)
        .then(response => {
            let {errcode, payload : {senderAdminEmail, receiverAdminEmail, notifyAboutNewUser, supportEmail} = {}} = response
            if (errcode === 200) {
                dispatch({
                    type : ADMIN_SETTINGS_SUCCESS,
                    payload : {
                        senderAdminEmail,
                        receiverAdminEmail,
                        notifyAboutNewUser,
                        supportEmail,
                    }
                })
            } else {
                dispatch({
                    type : ADMIN_SETTINGS_FAIL
                })
            }
        })
}

export const handleUpdateAdminSettings = (params = {}) => {
    let {senderAdminEmail, receiverAdminEmail, supportEmail, notifyAboutNewUser} = params

    let data = new FormData()
    data.append('params', JSON.stringify({
        senderAdminEmail,
        receiverAdminEmail,
        supportEmail,
        notifyAboutNewUser,
    }))

    return RestService.bodyAsFormData(ajaxUrls.adminUpdateParams, data)
        .then(middleReduxFormThen)
        .then(response => {
            let {errcode, message = ""} = response

            if (errcode === 200) {
                return 'ok'
            } else {
                throw new SubmissionError({
                    _error : formatErrorMessage(message),
                })
            }
        })
}


// WEBPACK FOOTER //
// ./yii2/frontend/frontend-assets/src/modules/app.js